import React, { useEffect, useState } from 'react';
import {
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Paper,
  Box,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@material-ui/core';
import {
  PictureAsPdf,
  Refresh,
  LocalShipping,
  CheckCircle,
  RemoveRedEye,
  Archive,
  ShoppingCart,
  Business,
  FilterList
} from '@material-ui/icons';
import MaterialTable from 'material-table';
import { makeStyles } from "@material-ui/core/styles";
import { Autocomplete } from '@material-ui/lab';
import { toast } from "react-toastify";
import { useSelector } from 'react-redux';
import BonService from '../../services/BonService';
import FournisseurService from '../../services/FournisseurService';
import NumCommandeService from '../../services/NumCommandeService';
import { generateCommandesPDF } from '../../utils/pdfUtils';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: theme.spacing(3),
  },
  header: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a857e 100%)',
    color: 'white',
    borderRadius: '16px',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: '0 8px 32px rgba(34, 109, 104, 0.2)',
  },
  title: {
    fontWeight: 700,
    fontSize: '1.8rem',
    textAlign: 'center',
    marginBottom: theme.spacing(1),
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    fontSize: '1rem',
  },
  statsContainer: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    borderRadius: '16px',
    padding: theme.spacing(2),
    textAlign: 'center',
    transition: 'all 0.3s ease',
    border: '1px solid #e0e0e0',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(34, 109, 104, 0.15)',
    },
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 8px',
    color: 'white',
    fontSize: 20,
  },
  statNumber: {
    fontWeight: 700,
    fontSize: '1.5rem',
    marginBottom: theme.spacing(0.5),
  },
  statLabel: {
    color: '#666',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  filtersContainer: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e0e0e0',
  },
  filterSection: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  depotButton: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a857e 100%)',
    color: 'white',
    borderRadius: '10px',
    padding: theme.spacing(1, 2),
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #1e615d 0%, #26746e 100%)',
      transform: 'translateY(-1px)',
    },
  },
  commanderButton: {
    background: 'linear-gradient(135deg, #f26522 0%, #ff8c42 100%)',
    color: 'white',
    fontSize: '0.75rem',
    padding: '4px 8px',
    minWidth: 'auto',
    borderRadius: '6px',
    '&:hover': {
      background: 'linear-gradient(135deg, #e55a1a 0%, #ff7b2e 100%)',
    },
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e0e0e0',
  },
  chip: {
    fontWeight: 600,
    fontSize: '0.75rem',
    '&.depot': {
      backgroundColor: '#E8EAF6',
      color: '#3f51b5',
    },
    '&.pending': {
      backgroundColor: '#FFF3CD',
      color: '#856404',
    },
    '&.commande': {
      backgroundColor: '#D4EDDA',
      color: '#155724',
    },
  },
  loadingContainer: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  loadingSpinner: {
    color: '#226D68',
    marginBottom: theme.spacing(2),
  },
  detailDialog: {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      padding: theme.spacing(1),
    },
  },
  dialogTitle: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a857e 100%)',
    color: 'white',
    padding: theme.spacing(3),
    '& h2': {
      fontWeight: 600,
    },
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: '1px solid #eee',
  },
  detailLabel: {
    fontWeight: 600,
    color: '#666',
  },
  detailValue: {
    fontWeight: 500,
    color: '#333',
  },
  exportButton: {
    background: 'linear-gradient(135deg, #dc3545 0%, #e74c3c 100%)',
    color: 'white',
    borderRadius: '10px',
    padding: theme.spacing(1, 2),
    fontWeight: 600,
    textTransform: 'none',
    marginLeft: theme.spacing(1),
    '&:hover': {
      background: 'linear-gradient(135deg, #c82333 0%, #d63031 100%)',
      transform: 'translateY(-1px)',
    },
  },
}));

const DepotCommandes = () => {
  const classes = useStyles();
  const user = useSelector((state) => state.auth);
  
  const [commandes, setCommandes] = useState([]);
  const [filteredCommandes, setFilteredCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [nums, setNums] = useState([]);
  const [selectedFournisseur, setSelectedFournisseur] = useState('');
  const [selectedNumero, setSelectedNumero] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedCommande, setSelectedCommande] = useState(null);
  
  const [stats, setStats] = useState({
    totalDepot: 0,
    total: 0,
    pending: 0,
    commande: 0,
  });

  // Chargement initial des données
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Utiliser la nouvelle méthode pour récupérer uniquement les commandes DEPOT
      const depotResponse = await BonService.getDepotCommandes();
      
      // Si la méthode getDepotCommandes n'existe pas, utiliser getBonsByStatus
      // ou filtrer manuellement les résultats
      let depotCommandes = [];
      
      if (depotResponse && depotResponse.data) {
        depotCommandes = depotResponse.data;
      } else {
        // Fallback: récupérer toutes les commandes et filtrer
        const allResponse = await BonService.getBons();
        depotCommandes = allResponse.data.filter(cmd => cmd.etat === 'DEPOT');
      }
      
      setCommandes(depotCommandes);
      setFilteredCommandes(depotCommandes);
      
      // Récupérer les autres données en parallèle
      const [fournisseursResponse, numsResponse, allCommandesResponse] = await Promise.all([
        FournisseurService.getFournisseurs(),
        NumCommandeService.getNumbers(),
        BonService.getBons() // Pour les statistiques globales
      ]);

      setFournisseurs(fournisseursResponse.data);
      setNums(numsResponse.data);

      // Calculer les statistiques
      const allCommandes = allCommandesResponse.data;
      setStats({
        totalDepot: depotCommandes.length,
        total: allCommandes.length,
        pending: allCommandes.filter(cmd => cmd.etat === 'PENDING').length,
        commande: allCommandes.filter(cmd => cmd.etat === 'COMMANDE').length,
      });
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Filtrage des commandes
  useEffect(() => {
    let filtered = commandes;

    // Filtre par fournisseur
    if (selectedFournisseur) {
      filtered = filtered.filter(cmd => 
        cmd.idFournisseur && cmd.idFournisseur.toString() === selectedFournisseur.toString()
      );
    }

    // Filtre par numéro
    if (selectedNumero) {
      filtered = filtered.filter(cmd => 
        cmd.idNumero && cmd.idNumero.toString() === selectedNumero.toString()
      );
    }

    // Filtre par recherche textuelle
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(cmd =>
        (cmd.nomProd && cmd.nomProd.toLowerCase().includes(term)) ||
        (cmd.nomFournisseur && cmd.nomFournisseur.toLowerCase().includes(term)) ||
        (cmd.numero && cmd.numero.toLowerCase().includes(term))
      );
    }

    setFilteredCommandes(filtered);
  }, [commandes, selectedFournisseur, selectedNumero, searchTerm]);

  const handleDepot = async () => {
    if (selectedRows.length === 0) {
      toast.warning('Veuillez sélectionner au moins une commande');
      return;
    }

    try {
      setLoading(true);
      // Envoyer les données sous forme d'objet { commandes: [...] }
      const response = await BonService.saveDepot({ commandes: selectedRows });
      
      if (response.data && response.data.success) {
        toast.success(`Dépôt traité avec succès (${response.data.details?.lignes_modifiees || selectedRows.length} commandes)`);
        fetchData(); // Rafraîchir les données
        setSelectedRows([]); // Réinitialiser la sélection
      } else {
        toast.error(response.data?.message || 'Erreur lors du traitement du dépôt');
      }
    } catch (error) {
      console.error('Erreur lors du traitement du dépôt:', error);
      toast.error(error.response?.data?.message || 'Erreur lors du traitement du dépôt');
    } finally {
      setLoading(false);
    }
  };

//   const handleCommander = async (commande) => {
//     try {
//       setLoading(true);
//       const response = await BonService.saveAllCommande([commande]);
      
//       if (response.data) {
//         toast.success('Commande validée avec succès');
//         fetchData(); // Rafraîchir les données
//       }
//     } catch (error) {
//       console.error('Erreur lors de la commande:', error);
//       toast.error('Erreur lors de la validation de la commande');
//     } finally {
//       setLoading(false);
//     }
//   };

const handleCommander = async (commandesArray) => {
    // Si on passe une seule commande (depuis le bouton dans le tableau)
    if (!Array.isArray(commandesArray)) {
      commandesArray = [commandesArray];
    }
    
    if (commandesArray.length === 0) {
      toast.warning('Aucune commande à traiter');
      return;
    }
  
    try {
      setLoading(true);
      const response = await BonService.saveAllCommande(commandesArray);
      
      if (response.data) {
        toast.success(`${commandesArray.length} commande(s) validée(s) avec succès`);
        fetchData(); // Rafraîchir les données
        setSelectedRows([]); // Réinitialiser la sélection
      }
    } catch (error) {
      console.error('Erreur lors de la commande:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la validation des commandes');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    if (filteredCommandes.length === 0) {
      toast.warning("Aucune commande à exporter");
      return;
    }

    try {
      const fileName = generateCommandesPDF(filteredCommandes, stats);
      toast.success(`PDF "${fileName}" généré avec succès`, {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const handleViewDetails = (commande) => {
    setSelectedCommande(commande);
    setDetailDialogOpen(true);
  };

  const columns = [
    {
      title: 'ID',
      field: 'idBonDeComdeMat',
      width: 80,
      headerStyle: { fontWeight: 600 },
    },
    {
      title: 'NUMÉRO',
      field: 'numero',
      headerStyle: { fontWeight: 600 },
    },
    {
      title: 'PRODUIT',
      field: 'nomProd',
      headerStyle: { fontWeight: 600 },
    },
    {
      title: 'FOURNISSEUR',
      field: 'nomFournisseur',
      headerStyle: { fontWeight: 600 },
    },
    {
      title: 'QUANTITÉ LIVRÉE',
      field: 'quan_livree',
      headerStyle: { fontWeight: 600 },
      render: rowData => (
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {rowData.quan_livree || 0}
        </Typography>
      ),
    },
    {
      title: 'PRIX UNITAIRE',
      field: 'prixUnitaire',
      headerStyle: { fontWeight: 600 },
      render: rowData => (
        <Typography variant="body2">
          {parseFloat(rowData.prixUnitaire || 0).toFixed(2)} Fcfa
        </Typography>
      ),
    },
    {
      title: 'PRIX PUBLIC',
      field: 'prixPublic',
      headerStyle: { fontWeight: 600 },
      render: rowData => (
        <Typography variant="body2">
          {parseFloat(rowData.prixPublic || 0).toFixed(2)} Fcfa
        </Typography>
      ),
    },
    {
      title: 'STATUT',
      field: 'etat',
      headerStyle: { fontWeight: 600 },
      render: rowData => (
        <Chip
          label={rowData.etat || 'DEPOT'}
          className={`${classes.chip} ${(rowData.etat || 'depot').toLowerCase()}`}
          size="small"
        />
      ),
    },
    {
      title: 'DATE',
      field: 'dateComde',
      headerStyle: { fontWeight: 600 },
      render: rowData => (
        <Typography variant="body2">
          {rowData.dateComde ? new Date(rowData.dateComde).toLocaleDateString() : 'N/A'}
        </Typography>
      ),
    },
    {
      title: 'ACTIONS',
      field: 'actions',
      headerStyle: { fontWeight: 600 },
      sorting: false,
      render: rowData => (
        <Box display="flex" gap={1}>
          <Tooltip title="Voir détails">
            <IconButton
              size="small"
              onClick={() => handleViewDetails(rowData)}
              style={{ color: '#226D68' }}
            >
              <RemoveRedEye fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Commander">
            <Button
              className={classes.commanderButton}
              startIcon={<ShoppingCart />}
              onClick={() => handleCommander(rowData)}
              variant="contained"
              size="small"
            >
              Commander
            </Button>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading && commandes.length === 0) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={60} className={classes.loadingSpinner} />
        <Typography variant="h6" style={{ color: '#666', fontWeight: 600 }}>
          Chargement des commandes en dépôt...
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
    
        {/* Statistiques */}
        <Grid container spacing={2} className={classes.statsContainer}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.statCard}>
              <div className={classes.statIcon} style={{ background: 'linear-gradient(135deg, #226D68 0%, #2a857e 100%)' }}>
                <Archive />
              </div>
              <Typography variant="h5" className={classes.statNumber} style={{ color: '#226D68' }}>
                {stats.totalDepot}
              </Typography>
              <Typography variant="caption" className={classes.statLabel}>
                En Dépôt
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.statCard}>
              <div className={classes.statIcon} style={{ background: 'linear-gradient(135deg, #f39c12 0%, #f1c40f 100%)' }}>
                <LocalShipping />
              </div>
              <Typography variant="h5" className={classes.statNumber} style={{ color: '#f39c12' }}>
                {stats.pending}
              </Typography>
              <Typography variant="caption" className={classes.statLabel}>
                En Attente
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.statCard}>
              <div className={classes.statIcon} style={{ background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)' }}>
                <CheckCircle />
              </div>
              <Typography variant="h5" className={classes.statNumber} style={{ color: '#27ae60' }}>
                {stats.commande}
              </Typography>
              <Typography variant="caption" className={classes.statLabel}>
                Commandées
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.statCard}>
              <div className={classes.statIcon} style={{ background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' }}>
                <Business />
              </div>
              <Typography variant="h5" className={classes.statNumber} style={{ color: '#3498db' }}>
                {stats.total}
              </Typography>
              <Typography variant="caption" className={classes.statLabel}>
                Total Commandes
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Filtres */}
        <Paper className={classes.filtersContainer}>
          <Box className={classes.filterSection}>
            <FilterList style={{ color: '#226D68' }} />
            <Typography variant="h6" style={{ color: '#226D68', fontWeight: 600 }}>
              Filtres
            </Typography>
            
            <TextField
              label="Rechercher..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1, minWidth: 200 }}
            />
            
            <Autocomplete
              options={fournisseurs}
              getOptionLabel={(option) => option.nomFournisseur || ''}
              onChange={(_, value) => setSelectedFournisseur(value?.idFournisseur || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Fournisseur"
                  variant="outlined"
                  size="small"
                  style={{ minWidth: 200 }}
                />
              )}
            />
            
            <Autocomplete
              options={nums}
              getOptionLabel={(option) => option.numero || ''}
              onChange={(_, value) => setSelectedNumero(value?.idNumero || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Numéro de commande"
                  variant="outlined"
                  size="small"
                  style={{ minWidth: 200 }}
                />
              )}
            />
            
            <IconButton onClick={fetchData} style={{ color: '#226D68' }}>
              <Refresh />
            </IconButton>

            {/* Bouton Export PDF */}
            <Button
              className={classes.exportButton}
              startIcon={<PictureAsPdf />}
              onClick={handleExportPDF}
              variant="contained"
              size="small"
            >
              Exporter PDF ({filteredCommandes.length})
            </Button>
          </Box>
        </Paper>

        {/* Actions */}
        <Box className={classes.actionButtons}>
          <Button
            className={classes.depotButton}
            startIcon={<Archive />}
            onClick={() => handleCommander(selectedRows)}
            disabled={selectedRows.length === 0 || loading}
            variant="contained"
          >
            Traiter les commandes ({selectedRows.length})
          </Button>
        </Box>

        {/* Tableau */}
        <Paper className={classes.tableContainer}>
          <MaterialTable
            columns={columns}
            data={filteredCommandes}
            title={
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6" style={{ color: '#226D68', fontWeight: 600 }}>
                  Liste des Commandes en Dépôt
                </Typography>
                <Button
                  className={classes.exportButton}
                  startIcon={<PictureAsPdf />}
                  onClick={handleExportPDF}
                  size="small"
                >
                  PDF
                </Button>
              </Box>
            }
            options={{
              selection: true,
              actionsColumnIndex: -1,
              pageSize: 10,
              pageSizeOptions: [5, 10, 20, 50],
              headerStyle: {
                backgroundColor: '#226D68',
                color: 'white',
                fontWeight: 600,
                fontSize: '14px',
              },
              rowStyle: (rowData) => ({
                backgroundColor: selectedRows.some(r => r.idBonDeComdeMat === rowData.idBonDeComdeMat) 
                  ? '#f0f9ff' 
                  : (rowData.tableData && rowData.tableData.id % 2 === 0 ? '#f8f9fa' : 'white'),
              }),
            }}
            onSelectionChange={(rows) => setSelectedRows(rows)}
            localization={{
              body: {
                emptyDataSourceMessage: 'Aucune commande en dépôt',
              },
              toolbar: {
                searchTooltip: 'Rechercher',
                searchPlaceholder: 'Rechercher...',
              },
              header: {
                actions: 'ACTIONS',
              },
            }}
          />
        </Paper>

        {/* Dialog de détails */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          className={classes.detailDialog}
          maxWidth="sm"
          fullWidth
        >
          {selectedCommande && (
            <>
              <DialogTitle className={classes.dialogTitle}>
                Détails de la commande
              </DialogTitle>
              <DialogContent dividers>
                <Box pt={2}>
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>ID:</span>
                    <span className={classes.detailValue}>{selectedCommande.idBonDeComdeMat}</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>Référence:</span>
                    <span className={classes.detailValue}>{selectedCommande.numero}</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>Produit:</span>
                    <span className={classes.detailValue}>{selectedCommande.nomProd}</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>Fournisseur:</span>
                    <span className={classes.detailValue}>{selectedCommande.nomFournisseur}</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>Quantité livrée:</span>
                    <span className={classes.detailValue}>{selectedCommande.quan_livree || 0}</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>Prix unitaire:</span>
                    <span className={classes.detailValue}>{selectedCommande.prixUnitaire || 0} Fcfa</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>Prix public:</span>
                    <span className={classes.detailValue}>{selectedCommande.prixPublic || 0} Fcfa</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>Date de commande:</span>
                    <span className={classes.detailValue}>
                      {selectedCommande.dateComde 
                        ? new Date(selectedCommande.dateComde).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>Date d'expiration:</span>
                    <span className={classes.detailValue}>
                      {selectedCommande.date_expiration 
                        ? new Date(selectedCommande.date_expiration).toLocaleDateString()
                        : 'Non spécifiée'}
                    </span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>Créé par:</span>
                    <span className={classes.detailValue}>{selectedCommande.speudo || 'N/A'}</span>
                  </div>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDetailDialogOpen(false)} color="primary">
                  Fermer
                </Button>
                <Button
                  className={classes.commanderButton}
                  onClick={() => {
                    handleCommander(selectedCommande);
                    setDetailDialogOpen(false);
                  }}
                  variant="contained"
                >
                  Commander
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </div>
  );
};

export default DepotCommandes;